/**
 * @file     HoverPhotoGallery.tsx
 * @readme   ./CLAUDE.md
 * @purpose  MLS card photo carousel that activates on hover (desktop only).
 *           Prefetches the media list when the card nears the viewport,
 *           crossfades between photos via dots and glass chevrons, and only
 *           switches once the next photo has loaded. Touch devices and
 *           reduced motion keep the single primary image or an instant swap.
 */
'use client';

import NextImage from 'next/image';
import Box from '@mui/material/Box';
import { mlsCardImageLoader, applyPhotoFallback } from '@/services/mls/mlsImageLoader';
import { loadListingMedia } from '@/services/mls/listingMediaLoader';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { fadeInOnLoad, motionTransition, photoZoom } from '@/core/motion';
import { overlayGlass } from './cardChrome';
import { layoutTokens } from '@/design-system/tokens/layout';
import { colorPalettesByMode } from '@/design-system/tokens/colors';

interface Props {
  listingKey: string;
  primarySrc: string;
  alt: string;
  priority?: boolean;
  /** next/image sizes; the 2-column hub grid passes a narrower phone width. */
  sizes?: string;
  maxPhotos?: number;
}

interface MediaEntry {
  mediaKey: string;
  src: string;
}

// Session-scoped cache so navigating around the grid doesn't re-fetch.
const mediaCache = new Map<string, MediaEntry[]>();
const inflight = new Map<string, Promise<MediaEntry[]>>();

async function loadMedia(listingKey: string, maxPhotos: number): Promise<MediaEntry[]> {
  const cached = mediaCache.get(listingKey);
  if (cached) return cached;
  const pending = inflight.get(listingKey);
  if (pending) return pending;

  const p = (async () => {
    try {
      // Batched media (DataLoader): visible tiles coalesce into one
      // /api/mls/media-list request instead of one full-detail fetch each.
      const media = await loadListingMedia(listingKey);
      const list = media
        .slice()
        .sort((a, b) => (a.mediaOrder ?? 999) - (b.mediaOrder ?? 999))
        .slice(0, maxPhotos)
        .map((m) => ({ mediaKey: m.mediaKey, src: `/api/mls/media/${m.mediaKey}` }));
      mediaCache.set(listingKey, list);
      return list;
    } catch {
      const empty: MediaEntry[] = [];
      mediaCache.set(listingKey, empty);
      return empty;
    } finally {
      inflight.delete(listingKey);
    }
  })();

  inflight.set(listingKey, p);
  return p;
}

const mediaSrcFor = (mediaKey: string) => `/api/mls/media/${encodeURIComponent(mediaKey)}`;

const Chevron = ({ dir }: { dir: 'prev' | 'next' }) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d={dir === 'prev' ? 'M14.5 6l-6 6 6 6' : 'M9.5 6l6 6-6 6'} />
  </svg>
);

export function HoverPhotoGallery({
  listingKey,
  primarySrc,
  alt,
  maxPhotos = 8,
  priority = false,
  sizes = '(max-width:600px) 100vw, (max-width:900px) 50vw, 400px',
}: Props) {
  const { theme } = useAppTheme();
  // A cached EMPTY list means the media lookup found nothing; the primary
  // photo still renders on its own.
  const [media, setMedia] = useState<MediaEntry[]>(() => mediaCache.get(listingKey) ?? []);
  const [active, setActive] = useState(0);
  // The slide the active one is fading in over. It stays opaque underneath
  // until the next change, so the crossfade never shows the placeholder.
  const [previous, setPrevious] = useState<number | null>(null);
  // A requested slide whose image has not loaded yet; it becomes active the
  // moment it has, so a click never fades to a blank frame.
  const [pending, setPending] = useState<number | null>(null);
  const [loaded, setLoaded] = useState<ReadonlySet<string>>(() => new Set(['primary']));
  // Highest slide index mounted so far: the one after the furthest visited,
  // so the next photo is always already loading.
  const [reach, setReach] = useState(1);
  const [hovered, setHovered] = useState(false);

  const supportsHover =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(hover: hover)').matches;

  const containerRef = useRef<HTMLDivElement>(null);

  // Prefetch the media list when the card nears the viewport so it is cached before hover.
  useEffect(() => {
    if (mediaCache.has(listingKey)) return;
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          observer.disconnect();
          loadMedia(listingKey, maxPhotos).then(setMedia);
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [listingKey, maxPhotos]);

  // Slide 0 is always the server-rendered primary photo (the LCP candidate)
  // under a stable key, so hover never remounts it. The rest are the other
  // photos in feed order.
  const slides = useMemo<MediaEntry[]>(() => {
    const rest = media.filter((m) => m.src !== primarySrc && mediaSrcFor(m.mediaKey) !== primarySrc);
    return [{ mediaKey: 'primary', src: primarySrc }, ...rest].slice(0, maxPhotos);
  }, [media, primarySrc, maxPhotos]);
  const total = slides.length;
  const navReady = total > 1;

  const activeRef = useRef(0);
  const commit = useCallback((target: number) => {
    if (activeRef.current !== target) {
      setPrevious(activeRef.current);
      activeRef.current = target;
      setActive(target);
    }
    setPending(null);
  }, []);

  const show = useCallback(
    (target: number) => {
      if (total === 0) return;
      const i = ((target % total) + total) % total;
      setReach((r) => Math.max(r, i + 1));
      if (i === active) {
        setPending(null);
        return;
      }
      if (loaded.has(slides[i]!.mediaKey)) commit(i);
      else setPending(i);
    },
    [total, active, loaded, slides, commit],
  );

  useEffect(() => {
    if (pending != null && slides[pending] && loaded.has(slides[pending]!.mediaKey)) commit(pending);
  }, [pending, loaded, slides, commit]);

  const markLoaded = useCallback((mediaKey: string) => {
    setLoaded((prev) => (prev.has(mediaKey) ? prev : new Set(prev).add(mediaKey)));
  }, []);

  const onEnter = useCallback(() => {
    setHovered(true);
    const cached = mediaCache.get(listingKey);
    if (cached && cached.length > 0) setMedia(cached);
  }, [listingKey]);

  const onLeave = useCallback(() => {
    setHovered(false);
    // Fade back to the primary photo rather than cutting to it.
    commit(0);
  }, [commit]);

  const next = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      show(active + 1);
    },
    [active, show],
  );
  const prev = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      show(active - 1);
    },
    [active, show],
  );

  // Controls render on hover straight away and fade in; navigation waits
  // until the media list has arrived (`navReady`).
  const showControls = supportsHover && hovered;
  const fade = motionTransition('opacity', 'base', 'standard');

  const isMounted = (i: number) =>
    i === 0 ||
    i === active ||
    i === previous ||
    i === pending ||
    (hovered && (i <= reach || i === total - 1));

  const arrowSx = (side: 'left' | 'right') =>
    ({
      position: 'absolute',
      top: '50%',
      [side]: 8,
      zIndex: 3,
      transform: 'translateY(-50%)',
      width: 32,
      height: 32,
      p: 0,
      borderRadius: `${layoutTokens.radius.pill}px`,
      ...overlayGlass(),
      color: theme.semanticColors.textPrimary,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: showControls ? 1 : 0,
      pointerEvents: showControls ? 'auto' : 'none',
      transition: motionTransition(['opacity', 'background-color', 'box-shadow'], 'base', 'standard'),
      '&:hover': { color: theme.semanticColors.interactivePrimary },
      '&:focus-visible': {
        outline: `2px solid ${theme.semanticColors.interactiveFocusRing}`,
        outlineOffset: 2,
      },
    }) as const;

  return (
    <Box
      ref={containerRef}
      data-gallery-active={active}
      onMouseEnter={supportsHover ? onEnter : undefined}
      onMouseLeave={supportsHover ? onLeave : undefined}
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        // Its own stack, so the card's photo scrim and pills layer above the
        // slides while the arrows stay clickable.
        isolation: 'isolate',
        // `&&` outranks the card's `[data-card-photo] img` rule, so the
        // crossfade and the hover zoom both keep their easing.
        '&& img[data-slide]': {
          transition: `${photoZoom.transition}, ${motionTransition('opacity', 'base', 'standard')}`,
        },
        // Reduced motion: photos swap instantly and controls appear without a fade.
        '@media (prefers-reduced-motion: reduce)': {
          '&& img[data-slide], && button, && [data-gallery-dots], && [data-gallery-dots] > *': {
            transition: 'none',
          },
        },
      }}
    >
      {/* Only the primary photo loads until hover, so a phone card costs one
          optimized image. On hover the next photo mounts ahead of the click. */}
      {slides.map((m, i) =>
        isMounted(i) ? (
          <NextImage
            key={m.mediaKey}
            src={m.src}
            loader={mlsCardImageLoader}
            alt={i === 0 ? alt : ''}
            fill
            priority={i === 0 && priority}
            ref={i === 0 && !priority ? fadeInOnLoad : undefined}
            sizes={sizes}
            // Card thumbnails: the proxy's 65 rung. PageSpeed flagged the 78
            // rung as under-compressed at card size; full photos keep theirs.
            quality={70}
            data-slide={i}
            style={{
              objectFit: 'cover',
              opacity: i === active || i === previous ? 1 : 0,
              zIndex: i === active ? 2 : i === previous ? 1 : 0,
              pointerEvents: 'none',
            }}
            onLoad={() => markLoaded(m.mediaKey)}
            onError={(e) => {
              applyPhotoFallback(e.currentTarget as HTMLImageElement);
              markLoaded(m.mediaKey);
            }}
          />
        ) : null,
      )}

      {supportsHover && navReady ? (
        <>
          <Box
            component="button"
            type="button"
            data-gallery-nav
            aria-label="Previous photo"
            aria-hidden={!showControls}
            tabIndex={showControls ? 0 : -1}
            onClick={prev}
            sx={arrowSx('left')}
          >
            <Chevron dir="prev" />
          </Box>
          <Box
            component="button"
            type="button"
            data-gallery-nav
            aria-label="Next photo"
            aria-hidden={!showControls}
            tabIndex={showControls ? 0 : -1}
            onClick={next}
            sx={arrowSx('right')}
          >
            <Chevron dir="next" />
          </Box>
          <Box
            data-gallery-dots
            sx={{
              position: 'absolute',
              bottom: 10,
              left: 0,
              right: 0,
              zIndex: 3,
              display: 'flex',
              justifyContent: 'center',
              gap: '6px',
              pointerEvents: 'none',
              opacity: showControls ? 1 : 0,
              transition: fade,
            }}
          >
            {slides.map((m, i) => (
              <Box
                key={`dot-${m.mediaKey}`}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: `${layoutTokens.radius.pill}px`,
                  // Photo overlays read on any photo in either theme: always light.
                  backgroundColor: colorPalettesByMode.light.neutral[0],
                  opacity: i === active ? 1 : 0.5,
                  transform: i === active ? 'scale(1.15)' : 'none',
                  transition: motionTransition(['opacity', 'transform'], 'base', 'standard'),
                }}
              />
            ))}
          </Box>
        </>
      ) : null}
    </Box>
  );
}
